// ViewResourceDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Bot, FolderOpen, Link, Plus, Check, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dialog for viewing resource details with sync/import actions
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.resource - The selected resource to display
 * @param {function} props.onSyncToAssetLibrary - Callback to sync AI resource to asset library
 * @param {function} props.onImportFromAssetLibrary - Callback to import asset to AI library
 * @param {function} props.onDelete - Callback to delete the resource
 * @param {function} props.onClose - Callback when close is clicked
 */
export const ViewResourceDialog = ({
  open,
  onOpenChange,
  resource,
  onSyncToAssetLibrary,
  onImportFromAssetLibrary,
  onDelete,
  onClose
}) => {
  const handleDownload = () => {
    const url = resource.preview_url || resource.file_url || `data:image/${resource.file_type};base64,${resource.base64_data}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${resource.name}.${resource.file_type || 'png'}`;
    link.click();
    toast.success('Image downloaded');
  };

  const getImageUrl = () => {
    return resource?.preview_url || resource?.file_url || 
      (resource?.base64_data ? `data:image/${resource.file_type};base64,${resource.base64_data}` : null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {resource?.name}
            {resource?.source_type === 'ai_library' && (
              <Badge className="bg-violet-600"><Bot className="h-3 w-3 mr-1" />AI Library</Badge>
            )}
            {resource?.source_type === 'asset_library' && (
              <Badge className="bg-blue-600"><FolderOpen className="h-3 w-3 mr-1" />Asset Library</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {resource?.category || resource?.asset_type} • Used {resource?.usage_count || 0} times by AI
          </DialogDescription>
        </DialogHeader>
        
        {resource && (
          <div className="space-y-4">
            {/* Image */}
            <div className="rounded-lg overflow-hidden border bg-slate-100">
              <img 
                src={getImageUrl()}
                alt={resource.name}
                className="w-full h-auto max-h-[400px] object-contain"
              />
            </div>
            
            {/* Details */}
            {resource.description && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-muted-foreground">{resource.description}</p>
              </div>
            )}
            
            {/* AI Description (for assets) */}
            {resource.ai_description && (
              <div className="bg-violet-50 p-3 rounded-lg">
                <p className="text-xs text-violet-600 font-medium mb-1">AI Analysis</p>
                <p className="text-sm text-muted-foreground">{resource.ai_description}</p>
              </div>
            )}
            
            {/* Tags */}
            {resource.tags?.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {resource.tags.map((tag, i) => (
                  <Badge key={i} variant="outline">{tag}</Badge>
                ))}
              </div>
            )}
            
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-muted-foreground">Uploaded by</p>
                <p className="font-medium">{resource.uploaded_by_name || 'Unknown'}</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-muted-foreground">File Type</p>
                <p className="font-medium uppercase">{resource.file_type || resource.asset_type}</p>
              </div>
            </div>

            {/* Sync Status / Actions */}
            {resource.source_type === 'ai_library' && !resource.synced_to_asset_library && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-800">Not synced to Asset Library</p>
                  <p className="text-xs text-amber-600">Sync to make available across the platform</p>
                </div>
                <Button 
                  size="sm" 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => onSyncToAssetLibrary(resource.id)}
                  data-testid="resource-sync-btn"
                >
                  <Link className="h-4 w-4 mr-1" />
                  Sync to Assets
                </Button>
              </div>
            )}
            {resource.source_type === 'ai_library' && resource.synced_to_asset_library && (
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <p className="text-sm text-green-800">Synced to Asset Library</p>
              </div>
            )}
            {resource.source_type === 'asset_library' && (
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-800">Asset Library Resource</p>
                  <p className="text-xs text-blue-600">Import to AI Library for AI agents to use</p>
                </div>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => onImportFromAssetLibrary(resource.id)}
                  data-testid="resource-import-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Import to AI Library
                </Button>
              </div>
            )}
          </div>
        )}
        
        <DialogFooter>
          {resource?.source_type === 'ai_library' && (
            <Button 
              variant="destructive" 
              onClick={() => onDelete(resource.id)}
              data-testid="resource-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="resource-close-btn">
            Close
          </Button>
          <Button onClick={handleDownload} data-testid="resource-download-btn">
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewResourceDialog;
